import React, { useState, useEffect } from 'react';
import './LektoratReviewModal.css';
import {
  startLektoratJob,
  pollLektoratUntilDone,
  fetchLektoratChanges,
  resolveLektoratChanges,
  downloadLektoratResolvedResult,
} from './LektoratUtils';

export const LektoratReviewModal = ({ isOpen, onClose, app, sessionId }) => {
  const [phase, setPhase] = useState('starting'); // starting|processing|loading_changes|review|resolving|need_file|error
  const [progress, setProgress] = useState({ message: '', progress: 0 });
  const [paragraphs, setParagraphs] = useState([]);
  const [decisions, setDecisions] = useState({});
  const [errorMessage, setErrorMessage] = useState('');

  const runPipeline = () => {
    let cancelled = false;

    const run = async () => {
      setPhase('starting');
      try {
        await startLektoratJob(app, sessionId);
        if (cancelled) return;

        setPhase('processing');
        await pollLektoratUntilDone(app, sessionId, {
          onProgress: (data) => { if (!cancelled) setProgress(data); },
          shouldStop: () => cancelled,
        });
        if (cancelled) return;

        setPhase('loading_changes');
        const paras = await fetchLektoratChanges(app, sessionId);
        if (cancelled) return;

        const initialDecisions = {};
        paras.forEach((p) => p.patches.forEach((patch) => { initialDecisions[patch.patch_id] = 'accept'; }));
        setDecisions(initialDecisions);
        setParagraphs(paras);
        setPhase('review');
      } catch (err) {
        if (cancelled) return;
        if (err.response?.status === 404) {
          setPhase('need_file');
        } else if (err.message !== 'cancelled') {
          setErrorMessage(err.response?.data?.detail || err.message || 'Unbekannter Fehler');
          setPhase('error');
        }
      }
    };

    run();
    return () => { cancelled = true; };
  };

  useEffect(() => {
    if (!isOpen) return;
    return runPipeline();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const setAllDecisions = (value) => {
    setDecisions((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((key) => { next[key] = value; });
      return next;
    });
  };

  const handleFinish = async () => {
    setPhase('resolving');
    try {
      await resolveLektoratChanges(app, sessionId, decisions);
      await downloadLektoratResolvedResult(app, sessionId, 'Lektorat_Änderungen_final.docx');
      onClose();
    } catch (err) {
      setErrorMessage(err.response?.data?.detail || err.message || 'Unbekannter Fehler');
      setPhase('error');
    }
  };

  if (!isOpen) return null;

  const totalPatches = paragraphs.reduce((sum, p) => sum + p.patches.length, 0);
  const acceptedCount = Object.values(decisions).filter((v) => v === 'accept').length;

  return (
    <div className="lektoratReviewOverlay" onClick={onClose}>
      <div className="lektoratReviewModal" onClick={(e) => e.stopPropagation()}>
        <button className="lektoratReviewClose" onClick={onClose}>×</button>
        <h2>Lektorat – Änderungsmodus</h2>

        {(phase === 'starting' || phase === 'processing' || phase === 'loading_changes') && (
          <div className="lektoratReviewProgress">
            <div className="lektoratReviewProgressBarTrack">
              <div
                className="lektoratReviewProgressBarFill"
                style={{ width: `${phase === 'loading_changes' ? 100 : (progress.progress || 0)}%` }}
              />
            </div>
            <div className="lektoratReviewProgressText">
              {phase === 'loading_changes' ? 'Änderungen werden geladen…' : (progress.message || 'Wird gestartet…')}
            </div>
          </div>
        )}

        {phase === 'need_file' && (
          <div className="lektoratReviewNeedFile">
            <p>
              Es ist noch kein Word-Dokument für diese Session hochgeladen. Bitte über den
              Upload-Button im Chat-Eingabefeld eine .docx-Datei hochladen und dann erneut versuchen.
            </p>
            <button className="lektoratReviewButton" onClick={() => runPipeline()}>
              Erneut versuchen
            </button>
          </div>
        )}

        {phase === 'error' && (
          <div className="lektoratReviewError">
            Fehler: {errorMessage}
          </div>
        )}

        {phase === 'review' && (
          <>
            <div className="lektoratReviewToolbar">
              <span className="lektoratReviewCount">
                {acceptedCount} von {totalPatches} Änderungen werden übernommen
              </span>
              <div className="lektoratReviewToolbarActions">
                <button className="lektoratReviewButton lektoratReviewButtonSecondary" onClick={() => setAllDecisions('accept')}>
                  Alle akzeptieren
                </button>
                <button className="lektoratReviewButton lektoratReviewButtonSecondary" onClick={() => setAllDecisions('reject')}>
                  Alle ablehnen
                </button>
                <button className="lektoratReviewButton" onClick={handleFinish}>
                  Fertigstellen &amp; herunterladen
                </button>
              </div>
            </div>

            <div className="lektoratReviewDocument">
              {paragraphs.map((para) => (
                <div key={para.index} className="lektoratReviewParagraphBlock">
                  <div
                    className="lektoratReviewParagraphOriginal"
                    dangerouslySetInnerHTML={{ __html: para.html }}
                  />
                  {para.patches.map((patch) => (
                    <div
                      key={patch.patch_id}
                      className={`lektoratReviewChangeCard ${decisions[patch.patch_id] === 'reject' ? 'isRejected' : 'isAccepted'}`}
                    >
                      <div className="lektoratReviewChangeDiff">
                        <del>{patch.original}</del>
                        <span className="lektoratReviewChangeArrow">→</span>
                        <ins>{patch.corrected}</ins>
                      </div>
                      {patch.reason && (
                        <div className="lektoratReviewChangeReason">{patch.reason}</div>
                      )}
                      <div className="lektoratReviewChangeActions">
                        <button
                          className={decisions[patch.patch_id] === 'accept' ? 'active' : ''}
                          onClick={() => setDecisions((d) => ({ ...d, [patch.patch_id]: 'accept' }))}
                        >
                          ✓ Übernehmen
                        </button>
                        <button
                          className={decisions[patch.patch_id] === 'reject' ? 'active' : ''}
                          onClick={() => setDecisions((d) => ({ ...d, [patch.patch_id]: 'reject' }))}
                        >
                          ✗ Verwerfen
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </>
        )}

        {phase === 'resolving' && (
          <div className="lektoratReviewProgress">
            <div className="lektoratReviewProgressText">Änderungen werden übernommen…</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LektoratReviewModal;
